import React, { useState, useEffect, useRef } from 'react';
import { LogIn as ClockIn, LogOut as ClockOut, LogOut, ChevronLeft, ChevronRight, ChevronRight as ChevR, Send, AlertTriangle, Megaphone, CalendarDays, Home, CheckSquare, Clock, CalendarCheck, Triangle, Search, Menu, X, Bell, HelpCircle, History, FileText, Users, Timer, Filter, Plus, RefreshCw, CheckCircle2, Circle, LayoutGrid, Zap } from 'lucide-react';
import { getSession, nowPKTDate, nowPKTTime, checkOfficeLocation, isWFHDay, isDefaultWFH, isLeaveOnTime, getDeviceInfo } from '../utils/auth';
import { calcScheduledMins, calcWorkedMins, dailyMinsOf, toMins, workHoursOf, fmtMins, getWorkingDays, getWorkingDaysExHolidays, isPublicHoliday, calcPayroll, leaveBalance, upcomingPayKey, prevPayKey, fmtDate, fmtPeriod, onPayroll } from '../utils/hr';
import CompanyAttendancePage from './CompanyAttendancePage';
import TasksPage from './TasksPage';
import ContentPage from './ContentPage';
import AdsPage from './AdsPage';
import { Badge, NotificationBell, EnableNotifications, DaysOffChoices } from '../components/UI';
import { fmtAmt } from '../utils/helpers';
import TodoWidget from '../components/TodoWidget';
import { punchClock } from '../utils/db';
import HolidayCalendar from './HolidayCalendar';
import { celebrate } from '../utils/celebrate';
import ReportsPage from './ReportsPage';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
function ym(y, m) { return `${y}-${String(m + 1).padStart(2, '0')}`; }

const statusColor = { present:'#8FB08A', absent:'#C08079', half:'var(--accent)', leave:'var(--blue)', holiday:'#C08079', ''  :'var(--text3)' };
const statusLabel = { present:'Present',       absent:'Absent',    half:'Half day',      leave:'Leave',       holiday:'Holiday',   ''  :'—' };

const TYPE_COLOR = { announcement:'blue', policy:'purple', reminder:'yellow', urgent:'red' };
const TYPE_LABEL = { announcement:'Announcement', policy:'Policy', reminder:'Reminder', urgent:'Urgent' };

// Shared input style
const inpSt = {
  width:'100%', padding:'8px 10px', fontSize:13,
  background:'var(--bg3)', border:'1px solid var(--border2)',
  borderRadius:'var(--radius)', color:'var(--text)', boxSizing:'border-box',
};

export default function EmployeePortal({ store, persist, onLogout, initialCalendarId }) {
  const session   = getSession();
  const empId     = session?.empId;
  const emp       = (store.employees || []).find(e => e.id === empId);
  const hrData    = store.hr || {};
  const overtimeReqs = (store.overtimeRequests || []).filter(r => r.empId === empId);
  const reportPerms = emp?.reportPerms || { socialMedia: false, perfMarketing: false };
  const empHR     = hrData[empId] || {};
  // Same schedule the payroll and the database use (falls back to DEFAULT_WORK_HOURS —
  // a bare 09:00–18:00 fallback here once gave Hamza the wrong finish time).
  const wh        = workHoursOf(store, { id: empId });
  const leaveReqs = (store.leaveRequests || []).filter(r => r.empId === empId);
  const updates   = store.updates || [];
  const holidays  = store.holidays;  // null = use defaults inside HolidayCalendar

  // Notifications for this employee
  const myNotifs = (store.notifications || [])
    .filter(n => n.empId === empId)
    .slice().sort((a,b) => (b.createdAt||'').localeCompare(a.createdAt||''));
  const markNotifsRead = () => persist({ ...store, notifications: (store.notifications||[]).map(n => n.empId===empId ? {...n, read:true} : n) });
  const clearNotifs    = () => persist({ ...store, notifications: (store.notifications||[]).filter(n => n.empId!==empId) });
  const [contentTarget, setContentTarget] = useState(initialCalendarId || null);
  // 'payroll': sent before payday — opens "My days off" to choose allowance or salary cut.
  const openNotif = (n) => {
    if (n?.kind === 'content' && n.taskId) { setContentTarget(n.taskId); goTab('content'); }
    else if (n?.kind === 'payroll') goTab('leave');
  };

  const today  = nowPKTDate();
  const isWFH  = isWFHDay(empId, today, hrData);

  // Resolve holiday list (mirrors HolidayCalendar defaults)
  const DEFAULT_PK_HOLIDAYS = [
    '2025-02-05','2025-03-23','2025-04-14','2025-04-15',
    '2025-05-01','2025-06-06','2025-06-07','2025-06-27',
    '2025-08-14','2025-09-05','2025-11-09','2025-12-25',
  ];
  const holidayDates = new Set(
    holidays ? holidays.map(h => h.date) : DEFAULT_PK_HOLIDAYS
  );
  const isTodayHoliday = holidayDates.has(today);
  const todayHolidayName = isTodayHoliday
    ? (holidays ? holidays.find(h => h.date === today)?.name || 'Public Holiday' : 'Public Holiday')
    : null;

  const [tab,       setTab]       = useState(initialCalendarId ? 'content' : (onPayroll(store, emp) ? 'home' : 'tasks'));
  const [ipStatus,     setIpStatus]    = useState('checking');
  const [gpsDistance,  setGpsDistance] = useState(null);
  const [gpsError,     setGpsError]    = useState(null);
  const [punchMsg,  setPunchMsg]  = useState('');
  const [punchBusy, setPunchBusy] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ date: today, type: 'full', reason: '' });
  const [leaveMsg,  setLeaveMsg]  = useState('');
  const [otForm,    setOtForm]    = useState({ date: today, startTime: '', endTime: '', reason: '' });
  const [otMsg,     setOtMsg]     = useState('');
  const [calYear,   setCalYear]   = useState(new Date().getFullYear());
  const [calMonth,  setCalMonth]  = useState(new Date().getMonth());
  const [mobileOpen, setMobileOpen] = useState(false);
  const sideRef = useRef(null);

  // Tick every 30s so "now" stays fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // IP check
  useEffect(() => {
    if (isWFH || isTodayHoliday) { setIpStatus('wfh'); return; }
    setIpStatus('checking');
    checkOfficeLocation().then(result => {
      if (result.error) {
        setIpStatus('remote');
        setGpsError(result.error);
      } else {
        setIpStatus(result.allowed ? 'office' : 'remote');
        setGpsDistance(result.distance);
      }
    });
  }, [isWFH, isTodayHoliday]); // eslint-disable-line

  // Auto-absent REMOVED (owner decision, 2026-08-19). It marked the employee absent
  // on portal open once now >= scheduled start + 3h, guarded only by today's punch
  // being present in the store — which it often wasn't yet, because the effect ran
  // against App.js's pre-load cached paint. That produced 22 absent days across 6
  // employees that all had a full clock-in AND clock-out. Attendance is now marked by
  // an admin in the HR tab only. `shouldAutoAbsent` in utils/auth.js is left in place
  // but is no longer called anywhere — do not re-wire it to an automatic writer.

  if (!emp) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text3)' }}>
      Profile not found. Contact your administrator.
    </div>
  );

  const att         = empHR.attendance || {};
  const tl          = empHR.timelog    || {};
  const todayEntry  = tl[today]        || {};
  const todayLeave  = leaveReqs.find(r => r.date === today && r.status === 'approved');
  const hasPunchIn  = !!todayEntry.in;
  const hasPunchOut = !!todayEntry.out;
  // Owner's rule: no clock-in once the scheduled finish has passed (punch_clock enforces
  // it on the server's clock too). Night shifts (finish <= start) aren't checked.
  const shiftOver   = !hasPunchIn && toMins(wh.end) > toMins(wh.start) && toMins(nowPKTTime()) >= toMins(wh.end);
  const shiftOverMsg = `Your shift ended at ${wh.end}. Clock-in is closed for today.`;

  // ── Punch In ────────────────────────────────────────────────────
  async function handlePunchIn() {
    if (shiftOver) { setPunchMsg('❌ ' + shiftOverMsg); return; }
    if (isTodayHoliday) {
      setPunchMsg(`❌ Today is a public holiday (${todayHolidayName}). Clock-in is disabled.`);
      return;
    }
    setPunchBusy(true); setPunchMsg('');
    if (!isWFH && !todayLeave) {
      setPunchMsg('⏳ Checking your location…');
      const gpsResult = await checkOfficeLocation();
      if (gpsResult.error) {
        setPunchMsg('❌ ' + gpsResult.error);
        setPunchBusy(false); return;
      }
      if (!gpsResult.allowed) {
        const dist = gpsResult.distance;
        setPunchMsg('❌ You are ' + dist + 'm from the office (max ' + (gpsResult.radius || 1000) + 'm). You must be on-site to clock in.');
        setPunchBusy(false); return;
      }
    }
    const t          = nowPKTTime();
    const deviceInfo = getDeviceInfo();
    const newTl      = { ...tl,  [today]: { ...todayEntry, in: t, device: deviceInfo } };
    const newAtt = { ...att, [today]: (att[today] === 'leave' || att[today] === 'half') ? att[today] : 'present' };
    const nextEmpHR = { ...empHR, timelog: newTl, attendance: newAtt };
    const updated = { ...store, hr: { ...hrData, [empId]: nextEmpHR } };
    persist(updated, { localOnly: true });   // show it now; nothing else to save
    // The punch (and today's 'present') is written by punch_clock — an atomic server-side
    // merge of just this one entry. No whole-store save: one made from a stale copy of
    // the data (e.g. tapped right after opening the app) reverted other people's work.
    try {
      await punchClock(empId, today, 'in', t, deviceInfo);
      setPunchMsg(`✅ Clocked in at ${t} PKT`);
      celebrate('clockin');
    } catch (e) {
      if (/shift_over/.test(e.message || '')) {
        persist(store, { localOnly: true });             // undo the optimistic clock-in
        setPunchMsg(`❌ Your shift ended at ${(e.message.split('shift_over:')[1] || wh.end).trim()}. Clock-in is closed for today.`);
      } else {
        setPunchMsg('⚠️ Clock-in didn’t reach the server — check your connection and try again.');
      }
    }
    setPunchBusy(false);
  }

  // ── Punch Out ───────────────────────────────────────────────────
  async function handlePunchOut() {
    if (isTodayHoliday) {
      setPunchMsg(`❌ Today is a public holiday (${todayHolidayName}). Clock-out is disabled.`);
      return;
    }
    setPunchBusy(true);
    const t = nowPKTTime();
    const newTl = { ...tl, [today]: { ...todayEntry, out: t } };
    const nextEmpHR = { ...empHR, timelog: newTl };
    const updated = { ...store, hr: { ...hrData, [empId]: nextEmpHR } };
    persist(updated, { localOnly: true });   // show it now; punch_clock saves it
    try {
      await punchClock(empId, today, 'out', t, null);
      setPunchMsg(`✅ Clocked out at ${t} PKT`);
      celebrate('clockout');
    } catch (e) {
      setPunchMsg('⚠️ Clock-out didn’t reach the server — check your connection and try again.');
    }
    setPunchBusy(false);
  }

  // ── Leave submit ─────────────────────────────────────────────────
  function submitLeave() {
    if (!leaveForm.reason.trim()) { setLeaveMsg('Please provide a reason.'); return; }
    if (holidayDates.has(leaveForm.date)) {
      setLeaveMsg('⚠️ That date is a public holiday — no leave request needed.');
      return;
    }
    const now2   = new Date().toISOString();
    const onTime = isLeaveOnTime(now2, leaveForm.date, wh.start);
    const req    = {
      id: Date.now().toString(), empId, empName: emp.name,
      date: leaveForm.date, type: leaveForm.type, reason: leaveForm.reason.trim(),
      requestedAt: now2, onTime, status: 'pending',
    };
    persist({ ...store, leaveRequests: [...(store.leaveRequests || []), req] });
    setLeaveForm({ date: today, type: 'full', reason: '' });
    setLeaveMsg(onTime
      ? '✅ Leave request submitted.'
      : '⚠️ Submitted with less than 12 hours’ notice before clock-in.'
    );
  }

  // ── Overtime submit ───────────────────────────────────────────────
  function submitOvertime() {
    if (!otForm.startTime || !otForm.endTime || !otForm.reason.trim()) {
      setOtMsg('Please fill in all fields.'); return;
    }
    if (otForm.endTime <= otForm.startTime) {
      setOtMsg('End time must be after start time.'); return;
    }
    const req = {
      id: Date.now().toString(), empId, empName: emp.name,
      date: otForm.date, startTime: otForm.startTime, endTime: otForm.endTime,
      reason: otForm.reason.trim(),
      requestedAt: new Date().toISOString(),
      status: 'pending',
    };
    persist({ ...store, overtimeRequests: [...(store.overtimeRequests || []), req] });
    setOtForm({ date: today, startTime: '', endTime: '', reason: '' });
    setOtMsg('✅ Overtime request submitted for approval.');
  }

  const ymStr       = ym(calYear, calMonth);
  const workingDays = getWorkingDays(ymStr);
  const todayStatus = isTodayHoliday ? 'holiday' : att[today] || '';

  const hasReports = reportPerms.socialMedia || reportPerms.perfMarketing;
  const isPerfMkt = [emp?.role, ...(emp?.roles || [])].filter(Boolean).some(r => ['Performance Marketer', 'Performance Marketer - STR'].includes(r));
  const ALL_TABS = [
    { id: 'home',       label: 'Dashboard',         icon: Home },
    { id: 'tasks',      label: 'Tasks',             icon: CheckSquare },
    { id: 'content',    label: 'Creative Studio',   icon: LayoutGrid },
    { id: 'clockin',    label: 'Clock In/Out',      icon: Clock },
    { id: 'history',    label: 'My History',        icon: History },
    { id: 'leave',      label: 'Leave Requests',    icon: CalendarCheck },
    { id: 'updates',    label: `Updates`,           icon: Megaphone },
    { id: 'calendar',   label: 'Holidays',          icon: CalendarDays },
    ...(hasReports ? [{ id: 'reports', label: 'Reports', icon: FileText }] : []),
    ...(isPerfMkt ? [{ id: 'ads', label: 'Ads', icon: Zap }] : []),
    { id: 'overtime',   label: 'Overtime',          icon: Timer },
    { id: 'attendance', label: 'Company Attendance', icon: Users },
  ];
  // No working hours or salary (an AI agent / freelancer): work tabs only — no clock-in,
  // hours, days off, leave, overtime or attendance.
  const NO_PAY_TABS = new Set(['home', 'clockin', 'history', 'leave', 'overtime', 'attendance']);
  const TABS = onPayroll(store, emp) ? ALL_TABS : ALL_TABS.filter(t => !NO_PAY_TABS.has(t.id));

  const goTab = (id) => { setTab(id); setMobileOpen(false); setPunchMsg(''); setLeaveMsg(''); setOtMsg(''); };
  const initials = emp.name.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
  const navLabel = TABS.find(t=>t.id===tab)?.label || 'Dashboard';

  return (
    <div className="app-shell">
      {/* Sidebar */}
      <aside ref={sideRef} className={`sidebar${mobileOpen?' open':''}`}>
        <div className="sidebar-brand str-lockup">
          <div className="str-mark">STR</div>
          <div className="str-hair"></div>
          <div className="str-desc">Employee Portal</div>
        </div>
        <nav className="nav-list">
          {TABS.map(({id,label,icon:Icon})=>(
            <button key={id} onClick={()=>goTab(id)} className={`nav-item${tab===id?' active':''}`}>
              <Icon size={18}/>{label}
              {id==='updates'&&updates.length>0&&<span style={{marginLeft:'auto',fontSize:10,fontWeight:700,background:'var(--accent)',color:'#0A0A0C',borderRadius:'var(--radius)',padding:'1px 7px'}}>{updates.length}</span>}
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="side-user">
            <div className="uav">{initials}</div>
            <div className="uinfo">
              <div className="uname">{emp.name}</div>
              <div className="urole">{emp.role||'Employee'}</div>
            </div>
            <button onClick={onLogout} title="Sign out" style={{background:'transparent',border:'none',cursor:'pointer',color:'var(--text3)',display:'flex',padding:4}}>
              <LogOut size={17}/>
            </button>
          </div>
        </div>
      </aside>
      {mobileOpen && <div onClick={()=>setMobileOpen(false)} style={{position:'fixed',inset:0,background:'rgba(10,10,12,0.6)',zIndex:55}}/>}

      <div className="content-area">
        {/* Top bar */}
        <header className="topbar">
          <button className="hamburger" onClick={()=>setMobileOpen(o=>!o)} style={{background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:8,cursor:'pointer',color:'var(--text2)',alignItems:'center'}}>
            {mobileOpen?<X size={18}/>:<Menu size={18}/>}
          </button>
          <div className="topsearch">
            <Search size={16}/>
            <input placeholder="Search resources…" readOnly/>
          </div>
          <div style={{flex:1}}/>
          {isTodayHoliday
            ? <span className="hide-mobile" style={{ fontSize: 11, fontWeight: 600, padding: '4px 11px', borderRadius:'var(--radius)', background: 'var(--red-bg)', color: '#C08079' }}>🎉 Holiday</span>
            : <span className="hide-mobile" style={{ fontSize: 11, fontWeight: 600, padding: '4px 11px', borderRadius:'var(--radius)', background: isWFH ? 'var(--blue-bg)' : 'var(--green-bg)', color: isWFH ? 'var(--blue)' : '#8FB08A' }}>{isWFH ? '🏠 WFH' : '🏢 Office'}</span>
          }
          <NotificationBell items={myNotifs} onMarkRead={markNotifsRead} onClear={clearNotifs} onOpen={openNotif}/>
          <div style={{width:1,height:24,background:'var(--border)'}} className="hide-mobile"/>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{textAlign:'right'}} className="hide-mobile">
              <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{emp.name}</div>
              <div style={{fontFamily:'var(--mono)',fontSize:10,fontWeight:400,letterSpacing:'0.05em',textTransform:'uppercase',color:'var(--text3)'}}>{emp.role||'Employee'}</div>
            </div>
            <div className="uav" style={{width:38,height:38,borderRadius:'50%',background:'var(--primary)',color:'#0A0A0C',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:13,fontFamily:'var(--font-ui)'}}>{initials}</div>
          </div>
        </header>

        <main className="page-main">
        <EnableNotifications empId={empId}/>
        <div key={tab} className="page-anim">

        {/* ══ HOME ══ */}
        {tab === 'home' && (() => {
          const ymStr2 = `${calYear}-${String(calMonth+1).padStart(2,'0')}`;
          const holsArr2 = Array.isArray(store.holidays) ? store.holidays : [];
          const wDays2 = getWorkingDaysExHolidays(ymStr2, holsArr2);
          const schMins2 = calcScheduledMins(wh, wDays2);
          const wrkMins2 = calcWorkedMins(empHR.timelog, wDays2);
          const isAssignedTo = (t, id) => t.assignedToIds ? t.assignedToIds.includes(id) : t.assignedToId === id;
          const isArchivedTask = t => t.status==='Reviewed' || t.status==='Completed';
          const myTasks = (store.tasks || []).filter(t => isAssignedTo(t, empId) && !isArchivedTask(t) && t.dueDate >= today);
          const myOverdue = (store.tasks || []).filter(t => isAssignedTo(t, empId) && !isArchivedTask(t) && t.status!=='Ready for Review' && t.status!=='Client Review' && t.dueDate < today);
          const recentDone = (store.tasks || []).filter(t => isAssignedTo(t, empId) && isArchivedTask(t)).slice(-2);
          const taskRows = [...myOverdue, ...myTasks, ...recentDone];
          const pto = leaveBalance(store, emp, today);
          const balPct = pto.accrued>0 ? Math.max(6, Math.min(100, (pto.remaining/pto.accrued)*100)) : 0;
          const recentUpdates = (updates||[]).slice(0,2);
          const greeting = new Date().getHours()<12?'morning':new Date().getHours()<17?'afternoon':'evening';

          const STATUS_META = {
            'In Progress':      { color:'var(--accent)',  icon:RefreshCw,     label:'In Progress' },
            'Ready for Review': { color:'#C79A5B',        icon:CheckCircle2,  label:'Completed — Ready for Review' },
            'Client Review':    { color:'var(--accent)',    icon:CheckCircle2,  label:'Awaiting Client Approval' },
            'Reviewed':         { color:'#8FB08A',   icon:CheckCircle2,  label:'Client Approved' },
            'Completed':        { color:'#8FB08A',   icon:CheckCircle2,  label:'Client Approved' },
            'Changes Required': { color:'#C79A5B',  icon:AlertTriangle, label:'Changes Required' },
            'Not Started':      { color:'var(--text3)',   icon:Circle,        label:'Not Started' },
          };
          const prio = (t) => t.dueDate < today || t.dueDate === today ? {l:'High',bg:'rgba(192,128,121,0.15)',c:'#C08079'}
                            : (new Date(t.dueDate)-new Date(today))/86400000 <= 3 ? {l:'Medium',bg:'var(--bg4)',c:'var(--text2)'}
                            : {l:'Low',bg:'var(--bg4)',c:'var(--text3)'};
          const updIcon = { announcement:Megaphone, policy:FileText, reminder:Bell, urgent:AlertTriangle };

          return (
            <div style={{display:'flex',flexDirection:'column',gap:24}}>
              {/* Obsidian hero with Clock In */}
              <section className="aura emp-hero" style={{position:'relative',overflow:'hidden',background:'radial-gradient(120% 140% at 85% 0%, rgba(169,137,83,0.10) 0%, rgba(169,137,83,0) 50%), var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius-xl)',padding:'2.25rem 2.5rem'}}>
                <div style={{position:'absolute',left:0,top:0,bottom:0,width:1,background:'var(--accent)',opacity:0.55}}/>
                <div style={{display:'flex',flexDirection:'row',alignItems:'center',justifyContent:'space-between',gap:24,flexWrap:'wrap'}}>
                  <div>
                    <h2 style={{fontSize:38,fontFamily:'var(--serif)',fontWeight:400,color:'var(--text)',letterSpacing:'-0.01em',margin:0}}>Good {greeting}, {emp.name.split(' ')[0]}.</h2>
                    <p style={{fontSize:15,color:'var(--text2)',marginTop:10}}>It's {new Date().toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}. {hasPunchIn?(hasPunchOut?'You have clocked out for today.':'You are clocked in.'):'Ready to strategize for the day?'}</p>
                  </div>
                  {!hasPunchOut && shiftOver && (
                    <div style={{padding:'16px 24px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',color:'var(--text2)',fontSize:13,maxWidth:300}}>{shiftOverMsg}</div>
                  )}
                  {!hasPunchOut && !shiftOver && (
                    <button onClick={hasPunchIn?handlePunchOut:handlePunchIn} disabled={punchBusy} className={'clock-btn'+(!hasPunchIn?' breathe':'')}
                      style={{display:'flex',alignItems:'center',gap:12,padding:'18px 38px',
                        background:'var(--accent)',color:'#0A0A0C',border:'none',borderRadius:'var(--radius)',fontFamily:'var(--mono)',fontWeight:600,fontSize:18,letterSpacing:'0.04em',textTransform:'uppercase',
                        cursor:punchBusy?'wait':'pointer',
                        transition:'transform .15s',opacity:punchBusy?0.7:1}}
                      onMouseEnter={e=>e.currentTarget.style.transform='scale(1.02)'}
                      onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
                      <Timer size={24}/>{hasPunchIn?'Clock Out':'Clock In'}
                    </button>
                  )}
                  {hasPunchOut && (
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'16px 28px',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',color:'var(--text)'}}>
                      <CheckCircle2 size={22} color="#8FB08A"/>
                      <div>
                        <div className="t-label" style={{marginBottom:2}}>Today</div>
                        <div style={{fontSize:15,fontWeight:700,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums'}}>{todayEntry.in} – {todayEntry.out}</div>
                        {todayEntry.autoOut && todayEntry.autoOut===todayEntry.out && <div style={{fontSize:11,color:'#C79A5B',marginTop:2}}>Clocked out automatically at your finish time — you didn't clock out</div>}
                      </div>
                    </div>
                  )}
                </div>
                {punchMsg && <div style={{marginTop:16,fontSize:13,color:'var(--text)',background:'var(--bg3)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:'10px 14px'}}>{punchMsg}</div>}
              </section>

              {/* Bento grid: left col (leave + updates), right col (tasks) */}
              <div className="emp-dash-grid">
                {/* LEFT COLUMN */}
                <div style={{display:'flex',flexDirection:'column',gap:24}}>
                  {/* Paid days off bank */}
                  <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',boxShadow:'var(--shadow-card)',padding:'24px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
                      <div>
                        <div className="t-label" style={{marginBottom:6}}>Paid Days Off · this year</div>
                        <div className="fig-md" style={{fontWeight:700,color:'var(--text)',letterSpacing:'-0.02em'}}>{pto.remaining} <span style={{fontSize:16,fontWeight:400,color:'var(--text3)'}}>left</span></div>
                      </div>
                      <div style={{padding:12,background:'rgba(169,137,83,0.12)',color:'var(--accent2)',borderRadius:'var(--radius)'}}><CalendarCheck size={20}/></div>
                    </div>
                    <div style={{width:'100%',background:'var(--bg4)',height:8,borderRadius:'var(--radius)',overflow:'hidden'}}>
                      <div className="bar-grow" style={{background:'var(--accent)',height:'100%',borderRadius:'var(--radius)',width:`${balPct}%`,transition:'width .5s'}}/>
                    </div>
                    <p style={{fontSize:13,color:'var(--text3)',marginTop:14}}>{pto.used} of {pto.accrued} used · accrued since {fmtDate(pto.leaveYear)}</p>
                  </div>

                  {/* Recent updates */}
                  <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',boxShadow:'var(--shadow-card)',padding:'24px'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
                      <h3 style={{fontSize:18,fontFamily:'var(--serif)',fontWeight:400,color:'var(--text)',margin:0}}>Recent Updates</h3>
                    </div>
                    {recentUpdates.length===0
                      ? <p style={{fontSize:13,color:'var(--text3)'}}>No updates yet.</p>
                      : <div style={{display:'flex',flexDirection:'column',gap:20}}>
                          {recentUpdates.map(u=>{ const UI=updIcon[u.type]||Megaphone; return (
                            <div key={u.id} onClick={()=>goTab('updates')} style={{display:'flex',gap:14,cursor:'pointer'}}>
                              <div style={{width:46,height:46,borderRadius:'var(--radius)',background:'var(--bg3)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><UI size={20} color="var(--accent2)"/></div>
                              <div style={{minWidth:0}}>
                                <h4 style={{fontSize:14.5,fontWeight:600,color:'var(--text)',margin:0}}>{u.title}</h4>
                                <p style={{fontSize:13,color:'var(--text3)',margin:'3px 0 0',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{u.body}</p>
                                <span style={{fontSize:10,fontWeight:400,letterSpacing:'0.05em',textTransform:'uppercase',color:'var(--text3)',marginTop:6,display:'block',fontFamily:'var(--mono)'}}>{new Date(u.createdAt).toLocaleDateString('default',{day:'numeric',month:'short'})}</span>
                              </div>
                            </div>
                          );})}
                        </div>
                    }
                    <button onClick={()=>goTab('updates')} style={{width:'100%',marginTop:20,padding:'12px',border:'1px solid var(--border2)',borderRadius:'var(--radius)',background:'transparent',fontSize:13,fontWeight:600,color:'var(--text2)',cursor:'pointer',fontFamily:'var(--font)'}}>View All Updates</button>
                  </div>
                </div>

                {/* RIGHT COLUMN — My Tasks */}
                <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',boxShadow:'var(--shadow-card)',display:'flex',flexDirection:'column'}}>
                  <div style={{padding:'22px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                    <div>
                      <h3 style={{fontSize:18,fontFamily:'var(--serif)',fontWeight:400,color:'var(--text)',margin:0}}>My Tasks</h3>
                      <p style={{fontSize:13,color:'var(--text3)',margin:'3px 0 0'}}>You have {myTasks.length+myOverdue.length} task{myTasks.length+myOverdue.length===1?'':'s'} pending{myOverdue.length>0?` · ${myOverdue.length} overdue`:''}.</p>
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button onClick={()=>goTab('tasks')} style={{padding:'8px 14px',background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',fontSize:13,fontWeight:600,color:'var(--text2)',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontFamily:'var(--font)'}}><Filter size={15}/>Filter</button>
                      <button onClick={()=>goTab('tasks')} style={{padding:'8px 14px',background:'var(--primary)',border:'none',borderRadius:'var(--radius)',fontSize:13,fontWeight:600,color:'#0A0A0C',cursor:'pointer',display:'flex',alignItems:'center',gap:6,fontFamily:'var(--font)'}}><Plus size={15}/>New Task</button>
                    </div>
                  </div>
                  <div className="emp-task-scroll" style={{flex:1}}>
                    {taskRows.length===0
                      ? <div style={{padding:'3rem',textAlign:'center',color:'var(--text3)',fontSize:14}}>No tasks assigned. Enjoy the calm. 🌤️</div>
                      : <table style={{width:'100%',borderCollapse:'collapse',textAlign:'left'}}>
                          <thead>
                            <tr style={{background:'var(--bg3)'}}>
                              {['Task Details','Priority','Status','Deadline'].map(h=>(
                                <th key={h} style={{padding:'14px 24px',fontFamily:'var(--mono)',fontSize:10,fontWeight:400,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--text3)'}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {taskRows.map(t=>{
                              const done = t.status==='Completed' || t.status==='Reviewed';
                              const sm = STATUS_META[t.status]||STATUS_META['Not Started'];
                              const SIcon = sm.icon; const p = prio(t);
                              return (
                                <tr key={t.id} style={{borderTop:'1px solid var(--border)'}}>
                                  <td style={{padding:'18px 24px'}}>
                                    <div style={{display:'flex',alignItems:'center',gap:12,opacity:done?0.6:1}}>
                                      <span style={{width:8,height:8,borderRadius:'50%',background:done?'#8FB08A':t.status==='In Progress'?'var(--accent)':'var(--border2)',flexShrink:0}}/>
                                      <span style={{fontSize:14.5,fontWeight:600,color:'var(--text)',textDecoration:done?'line-through':'none'}}>{t.taskName}</span>
                                    </div>
                                  </td>
                                  <td style={{padding:'18px 24px'}}>
                                    <span style={{padding:'3px 9px',background:p.bg,color:p.c,fontSize:10,fontWeight:400,borderRadius:'var(--radius)',textTransform:'uppercase',letterSpacing:'0.1em',fontFamily:'var(--mono)'}}>{p.l}</span>
                                  </td>
                                  <td style={{padding:'18px 24px'}}>
                                    <span style={{display:'flex',alignItems:'center',gap:7,color:sm.color,fontSize:13.5,fontWeight:600}}>
                                      <SIcon size={15}/>{sm.label||t.status}
                                    </span>
                                  </td>
                                  <td style={{padding:'18px 24px',fontSize:13.5,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',color: t.dueDate<today?'#C08079':'var(--text3)'}}>{new Date(t.dueDate+'T00:00:00').toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                    }
                  </div>
                  <div style={{padding:'16px',borderTop:'1px solid var(--border)',textAlign:'center'}}>
                    <button onClick={()=>goTab('tasks')} style={{background:'none',border:'none',color:'var(--accent2)',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'var(--font)'}}>View All Task Boards</button>
                  </div>
                </div>
              </div>

              <TodoWidget store={store} persist={persist} empId={empId} />
            </div>
          );
        })()}

        {/* ══ TASKS ══ */}
        {tab === 'tasks' && (
          <TasksPage
            store={store}
            persist={persist}
            showToast={msg => { /* inline toast handler */ const el=document.createElement('div'); el.textContent=msg; el.style.cssText='position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--bg2);color:var(--text);padding:12px 18px;border-radius:2px;font-size:13px;z-index:9999;border:1px solid var(--border);border-left:2px solid var(--accent);font-family:var(--font)'; document.body.appendChild(el); setTimeout(()=>el.remove(),3000); }}
            session={{ role:'employee', empId }}
          />
        )}

        {/* ══ ADS ══ */}
        {tab === 'ads' && isPerfMkt && <AdsPage />}

        {/* ══ CONTENT ══ */}
        {tab === 'content' && (
          <ContentPage
            store={store}
            persist={persist}
            showToast={msg => { const el=document.createElement('div'); el.textContent=msg; el.style.cssText='position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--bg2);color:var(--text);padding:12px 18px;border-radius:2px;font-size:13px;z-index:9999;border:1px solid var(--border);border-left:2px solid var(--accent);font-family:var(--font)'; document.body.appendChild(el); setTimeout(()=>el.remove(),3000); }}
            session={{ role:'employee', empId }}
            openCalendarId={contentTarget}
            onOpenConsumed={()=>setContentTarget(null)}
          />
        )}

        {/* ══ CLOCK IN/OUT ══ */}
        {tab === 'clockin' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius-lg)', padding: '1.5rem', textAlign: 'center' }}>
              <div style={{ fontSize: 12, fontFamily: 'var(--mono)', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text3)', marginBottom: 4 }}>TODAY</div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', color: 'var(--text)', marginBottom: 2 }}>{today}</div>
              <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>{fmtMins(dailyMinsOf(wh))} a day · start whenever suits you (usually {wh.start} – {wh.end})</div>

              {/* Holiday banner */}
              {isTodayHoliday && (
                <div style={{ margin: '0 0 14px', padding: '10px 16px', background: 'var(--red-bg)', border: '1px solid rgba(248,113,113,0.3)', borderRadius:'var(--radius)', fontSize: 13, color: '#C08079', fontWeight: 500 }}>
                  🎉 {todayHolidayName} — public holiday. Clock-in/out disabled.
                </div>
              )}

              {/* Network / WFH status */}
              {!isTodayHoliday && !isWFH && !todayLeave && (
                <div style={{ marginBottom: 12, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                  color: ipStatus === 'office' ? '#8FB08A' : ipStatus === 'remote' ? '#C79A5B' : 'var(--text3)' }}>
                  {ipStatus === 'checking' && '⏳ Checking network…'}
                  {ipStatus === 'office'   && '✅ Office network detected'}
                  {ipStatus === 'remote'   && '⚠️ Office location not detected yet — we’ll re-check when you tap Clock In'}
                </div>
              )}
              {!isTodayHoliday && isWFH      && <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--blue)' }}>🏠 WFH day — network check bypassed</div>}
              {!isTodayHoliday && todayLeave  && <div style={{ marginBottom: 10, fontSize: 12, color: '#8FB08A' }}>✅ Leave approved for today</div>}

              {/* Status badge */}
              {todayStatus && (
                <div style={{ marginBottom: 14, display: 'inline-block', fontSize: 12, fontWeight: 600, padding: '3px 12px', borderRadius:'var(--radius)',
                  background: todayStatus === 'present' ? 'var(--green-bg)' : todayStatus === 'absent' ? 'var(--red-bg)' : todayStatus === 'holiday' ? 'var(--red-bg)' : todayStatus === 'leave' ? 'var(--blue-bg)' : 'var(--bg4)',
                  color: statusColor[todayStatus] || 'var(--text3)' }}>
                  {statusLabel[todayStatus] || todayStatus}
                </div>
              )}

              {/* Times */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 20 }}>
                {[['CLOCK IN', todayEntry.in, '#8FB08A'], ['CLOCK OUT', todayEntry.out, 'var(--accent)']].map(([lbl, val, col]) => (
                  <div key={lbl}>
                    <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{lbl}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', color: val ? col : 'var(--text3)' }}>{val || '—:—'}</div>
                  </div>
                ))}
              </div>

              {/* Punch buttons */}
              {!todayLeave && !isTodayHoliday && (
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={handlePunchIn} disabled={hasPunchIn || punchBusy || shiftOver} title={shiftOver ? shiftOverMsg : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 28px', fontSize: 14, fontWeight: 600, borderRadius:'var(--radius)', border: 'none',
                      cursor: (!hasPunchIn && !punchBusy && !shiftOver) ? 'pointer' : 'not-allowed',
                      background: hasPunchIn || shiftOver ? 'var(--bg4)' : '#8FB08A',
                      color: hasPunchIn || shiftOver ? 'var(--text3)' : '#0A0A0C',
                      opacity: (hasPunchIn || punchBusy || shiftOver) ? 0.7 : 1, transition: 'all 0.15s' }}>
                    <ClockIn size={16} />{punchBusy ? 'Checking…' : hasPunchIn ? 'Clocked In ✓' : shiftOver ? `Closed after ${wh.end}` : 'Clock In'}
                  </button>
                  <button onClick={handlePunchOut} disabled={!hasPunchIn || hasPunchOut}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 28px', fontSize: 14, fontWeight: 600, borderRadius:'var(--radius)', border: 'none',
                      cursor: (hasPunchIn && !hasPunchOut) ? 'pointer' : 'not-allowed',
                      background: hasPunchOut ? 'var(--bg4)' : hasPunchIn ? 'var(--accent)' : 'var(--bg4)',
                      color: hasPunchOut || !hasPunchIn ? 'var(--text3)' : '#0A0A0C',
                      opacity: (!hasPunchIn || hasPunchOut) ? 0.6 : 1, transition: 'all 0.15s' }}>
                    <ClockOut size={16} />{hasPunchOut ? 'Clocked Out ✓' : 'Clock Out'}
                  </button>
                </div>
              )}
              {punchMsg && (
                <div style={{ marginTop: 14, fontSize: 13, fontWeight: 500, color: punchMsg.startsWith('✅') ? '#8FB08A' : '#C08079' }}>{punchMsg}</div>
              )}
            </div>
          </div>
        )}

        {/* ══ HISTORY ══ */}
        {tab === 'history' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <button onClick={() => { if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); } else setCalMonth(m => m - 1); }}
                style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius:'var(--radius)', padding: 6, cursor: 'pointer', color: 'var(--text2)', display: 'flex' }}>
                <ChevronLeft size={15} />
              </button>
              <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', flex: 1, textAlign: 'center' }}>{MONTHS[calMonth]} {calYear}</span>
              <button onClick={() => { if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); } else setCalMonth(m => m + 1); }}
                style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius:'var(--radius)', padding: 6, cursor: 'pointer', color: 'var(--text2)', display: 'flex' }}>
                <ChevronRight size={15} />
              </button>
            </div>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow: 'hidden' }}>
              {workingDays.length === 0
                ? <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No working days.</div>
                : workingDays.map((date, i) => {
                  const a   = holidayDates.has(date) ? 'holiday' : att[date] || '';
                  const en  = tl[date] || {};
                  const lr  = leaveReqs.find(r => r.date === date);
                  return (
                    <div key={date} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: i < workingDays.length - 1 ? '1px solid var(--border)' : 'none', gap: 10 }}>
                      <div style={{ minWidth: 100, fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>
                        {new Date(date + 'T12:00:00').toLocaleDateString('default', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </div>
                      <div style={{ flex: 1, fontSize: 12, color: 'var(--text2)', fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums' }}>
                        {en.in  && <span style={{ marginRight: 10 }}>In: {en.in}</span>}
                        {en.out && <span>Out: {en.out}</span>}
                        {lr && <span style={{ fontSize: 11, color: 'var(--blue)', marginLeft: 8 }}>Leave ({lr.status})</span>}
                        {a === 'holiday' && <span style={{ fontSize: 11, color: '#C08079', marginLeft: 8 }}>
                          {holidays ? holidays.find(h => h.date === date)?.name || 'Public Holiday' : 'Public Holiday'}
                        </span>}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius:'var(--radius)',
                        background: a === 'present' ? 'var(--green-bg)' : a === 'absent' ? 'var(--red-bg)' : a === 'leave' ? 'var(--blue-bg)' : a === 'holiday' ? 'var(--red-bg)' : a === 'half' ? 'rgba(169,137,83,0.12)' : 'var(--bg4)',
                        color: statusColor[a] || 'var(--text3)' }}>
                        {statusLabel[a] || '—'}
                      </span>
                    </div>
                  );
                })}
            </div>
            {/* Summary */}
            {(() => {
              const present = workingDays.filter(d => att[d] === 'present').length;
              const absent  = workingDays.filter(d => att[d] === 'absent').length;
              const half    = workingDays.filter(d => att[d] === 'half').length;
              const leave   = workingDays.filter(d => att[d] === 'leave').length;
              const holiday = workingDays.filter(d => holidayDates.has(d)).length;
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginTop: 12 }}>
                  {[['Present',present,'#8FB08A'],['Absent',absent,'#C08079'],['Half',half,'var(--accent)'],['Leave',leave,'var(--blue)'],['Holiday',holiday,'#C08079']].map(([lbl,val,col]) => (
                    <div key={lbl} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius)', padding: '0.65rem', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 400, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text3)', marginBottom: 3 }}>{lbl}</div>
                      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', color: col }}>{val}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* ══ LEAVE REQUESTS ══ */}
        {tab === 'leave' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius-lg)', padding: '1.25rem' }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 14 }}>Request leave</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 400, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Date</label>
                  <input type="date" value={leaveForm.date} onChange={e => setLeaveForm(p => ({ ...p, date: e.target.value }))} style={inpSt} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 400, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Type</label>
                  <select value={leaveForm.type} onChange={e => setLeaveForm(p => ({ ...p, type: e.target.value }))} style={inpSt}>
                    <option value="full">Full day</option>
                    <option value="half">Half day</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 400, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Reason</label>
                <textarea value={leaveForm.reason} onChange={e => setLeaveForm(p => ({ ...p, reason: e.target.value }))} placeholder="Reason for leave…" rows={3}
                  style={{ ...inpSt, resize: 'vertical', fontFamily: 'var(--font)' }} />
              </div>
              {leaveForm.date && !isLeaveOnTime(new Date().toISOString(), leaveForm.date, wh.start) && !holidayDates.has(leaveForm.date) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.3)', borderRadius:'var(--radius)', padding: '9px 12px', marginBottom: 10 }}>
                  <AlertTriangle size={13} color="#C79A5B" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 12, color: '#C79A5B' }}>Less than 12 hours before your scheduled clock-in. Salary deduction may apply even if approved.</span>
                </div>
              )}
              {leaveForm.date && holidayDates.has(leaveForm.date) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: 'var(--red-bg)', border: '1px solid rgba(248,113,113,0.3)', borderRadius:'var(--radius)', padding: '9px 12px', marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: '#C08079' }}>🎉 That date is already a public holiday — no leave request needed.</span>
                </div>
              )}
              <button onClick={submitLeave} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 16px', fontSize: 13, fontWeight: 600, background: 'var(--accent)', color: '#0A0A0C', border: 'none', borderRadius:'var(--radius)', cursor: 'pointer' }}>
                <Send size={13} />Submit request
              </button>
              {leaveMsg && <div style={{ marginTop: 10, fontSize: 13, fontWeight: 500, color: leaveMsg.startsWith('✅') ? '#8FB08A' : '#C79A5B' }}>{leaveMsg}</div>}
            </div>

            {/* Days off this pay period — choose how each one is paid */}
            {(() => {
              const pcs = [calcPayroll(store, emp, upcomingPayKey(emp, today), today)];
              const prev = calcPayroll(store, emp, prevPayKey(pcs[0].key), today);
              if (!prev.paid && (prev.days || []).some(d => d.off > 0)) pcs.unshift(prev);
              const any = pcs.some(c => (c.days || []).some(d => d.off > 0));
              const choose = (date, v) => persist({ ...store, hr: { ...hrData, [empId]: { ...empHR, offChoice: { ...(empHR.offChoice || {}), [date]: v } } } });
              return (
                <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius-lg)', padding: '1.25rem' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 3 }}>My days off</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>Each day off is paid from your leave allowance, unless you choose a salary cut for it — then your allowance stays as it is. Never both. You'll get a reminder before payday; anything you don't choose comes from your allowance.</div>
                  {!any && <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 10 }}>No days off this pay period.</div>}
                  {pcs.map(c => (c.days || []).some(d => d.off > 0) && (
                    <div key={c.key} style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{fmtPeriod(c.period)}</div>
                      <DaysOffChoices calc={c} fmtDate={fmtDate} onChoose={choose} />
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Request history */}
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>All my requests</div>
              {leaveReqs.length === 0
                ? <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No requests yet.</div>
                : leaveReqs.slice().reverse().map(r => (
                  <div key={r.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>{r.date} · {r.type === 'full' ? 'Full day' : 'Half day'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: r.onTime ? 0 : 4 }}>{r.reason}</div>
                      {!r.onTime && <span style={{ fontSize: 10, color: '#C79A5B', background: 'rgba(251,146,60,0.12)', padding: '1px 7px', borderRadius:'var(--radius)', fontWeight: 600 }}>Late request</span>}
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius:'var(--radius)',
                        background: r.status === 'approved' ? 'var(--green-bg)' : r.status === 'rejected' ? 'var(--red-bg)' : 'var(--bg4)',
                        color: r.status === 'approved' ? '#8FB08A' : r.status === 'rejected' ? '#C08079' : 'var(--text3)' }}>
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </span>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* ══ UPDATES ══ */}
        {tab === 'updates' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {updates.length === 0
              ? (
                <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius-lg)', padding: '2.5rem', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                  No updates from management yet.
                </div>
              )
              : updates.map(u => (
                <div key={u.id} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius:'var(--radius-lg)', padding: '1.1rem 1.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <Megaphone size={14} color="var(--accent)" />
                    <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{u.title}</span>
                    <Badge label={TYPE_LABEL[u.type] || u.type} color={TYPE_COLOR[u.type] || 'gray'} />
                    {u.editedAt && <span style={{ fontSize: 10, color: 'var(--text3)' }}>edited</span>}
                  </div>
                  <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 8px', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{u.body}</p>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                    {new Date(u.createdAt).toLocaleDateString('default', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              ))
            }
          </div>
        )}

        {/* ══ HOLIDAYS CALENDAR ══ */}
        {tab === 'calendar' && (
          <HolidayCalendar store={store} persist={persist} showToast={() => {}} readOnly />
        )}

        {/* ══ OVERTIME ══ */}
        {tab === 'overtime' && (() => {
          // Same engine and period as payroll, so this card can't contradict the salary.
          const pc  = calcPayroll(store, emp, upcomingPayKey(emp, today), today);
          const net = pc.hoursWorkedMins - pc.requiredMins;
          const inpSt2  = { width:'100%',padding:'8px 10px',fontSize:13,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:'var(--radius)',color:'var(--text)',boxSizing:'border-box' };
          return (
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              {/* Hours summary — current pay period */}
              <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'1.25rem'}}>
                <div style={{fontWeight:600,fontSize:14,color:'var(--text)',marginBottom:3}}>Hours · {fmtPeriod(pc.period)}</div>
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:12}}>For the salary due {fmtDate(pc.period.dueDate)}.{!pc.complete && ' Days still to come are not counted against you.'} Public holidays and your days off are not required hours.</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:12}}>
                  {[
                    [pc.complete?'Required':'Required so far', fmtMins(pc.requiredMins), 'var(--text3)'],
                    ['Worked', fmtMins(pc.hoursWorkedMins), pc.hoursWorkedMins>=pc.requiredMins?'#8FB08A':'#C79A5B'],
                    [net>=0?'Ahead':'Short', net===0?'Even':fmtMins(Math.abs(net)), net>=0?'#8FB08A':'#C08079'],
                  ].map(([l,v,col])=>(
                    <div key={l} style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'10px 12px',textAlign:'center'}}>
                      <div style={{fontSize:10,fontFamily:'var(--mono)',color:'var(--text3)',fontWeight:400,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>{l}</div>
                      <div style={{fontSize:16,fontWeight:700,fontFamily:'var(--mono)',fontVariantNumeric:'lining-nums tabular-nums',color:col}}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{background:'var(--bg3)',borderRadius:'var(--radius)',padding:'12px 14px',fontSize:13,color:'var(--text2)'}}>
                  {pc.overtimeMins > 0
                    ? <>Overtime so far: <strong style={{color:'var(--accent)'}}>{fmtMins(pc.overtimeMins)}</strong> beyond your schedule = <strong style={{color:'#8FB08A'}}>+{fmtAmt(pc.overtimePay)}</strong> at 1.5×</>
                    : <>No overtime yet this month.</>}
                  <div style={{fontSize:12,color:'var(--text3)',marginTop:4}}>Overtime is paid at 1.5× your hourly rate, only for hours beyond this month's scheduled hours. Weekend or holiday work needs an approved request.</div>
                </div>
              </div>

              {/* Submit overtime request */}
              <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'1.25rem'}}>
                <div style={{fontWeight:600,fontSize:14,color:'var(--text)',marginBottom:12}}>Request overtime</div>
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:12}}>For work after you've clocked out, or on a weekend or holiday. All requests need admin approval.</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:10}}>
                  <div><label style={{display:'block',fontSize:11,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:5}}>Date</label>
                    <input type="date" value={otForm.date} onChange={e=>setOtForm(p=>({...p,date:e.target.value}))} style={inpSt2}/></div>
                  <div><label style={{display:'block',fontSize:11,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:5}}>Start time</label>
                    <input type="time" value={otForm.startTime} onChange={e=>setOtForm(p=>({...p,startTime:e.target.value}))} style={inpSt2}/></div>
                  <div><label style={{display:'block',fontSize:11,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:5}}>End time</label>
                    <input type="time" value={otForm.endTime} onChange={e=>setOtForm(p=>({...p,endTime:e.target.value}))} style={inpSt2}/></div>
                </div>
                <div style={{marginBottom:10}}>
                  <label style={{display:'block',fontSize:11,fontFamily:'var(--mono)',fontWeight:400,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:5}}>Reason</label>
                  <textarea value={otForm.reason} onChange={e=>setOtForm(p=>({...p,reason:e.target.value}))} placeholder="What work were you doing?" rows={2}
                    style={{...inpSt2,resize:'vertical',fontFamily:'var(--font)'}}/>
                </div>
                <button onClick={submitOvertime} style={{display:'flex',alignItems:'center',gap:7,padding:'8px 16px',fontSize:13,fontWeight:600,background:'var(--accent)',color:'#0A0A0C',border:'none',borderRadius:'var(--radius)',cursor:'pointer'}}>
                  Submit request
                </button>
                {otMsg && <div style={{marginTop:10,fontSize:13,color:otMsg.startsWith('✅')?'#8FB08A':'#C08079'}}>{otMsg}</div>}
              </div>

              {/* Past OT requests */}
              <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',overflow:'hidden'}}>
                <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',fontWeight:600,fontSize:13,color:'var(--text)'}}>My overtime requests</div>
                {overtimeReqs.length===0
                  ? <div style={{padding:'2rem',textAlign:'center',color:'var(--text3)',fontSize:13}}>No requests yet.</div>
                  : overtimeReqs.slice().reverse().map(r=>(
                    <div key={r.id} style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',display:'flex',gap:10,alignItems:'flex-start'}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:500,fontSize:13,color:'var(--text)',marginBottom:2}}>{r.date} · {r.startTime} – {r.endTime}</div>
                        <div style={{fontSize:12,color:'var(--text3)'}}>{r.reason}</div>
                      </div>
                      <span style={{fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:'var(--radius)',background:r.status==='approved'?'var(--green-bg)':r.status==='rejected'?'var(--red-bg)':'var(--bg4)',color:r.status==='approved'?'#8FB08A':r.status==='rejected'?'#C08079':'var(--text3)'}}>
                        {r.status.charAt(0).toUpperCase()+r.status.slice(1)}
                      </span>
                    </div>
                  ))
                }
              </div>
            </div>
          );
        })()}

        {/* ══ REPORTS ══ */}
        {tab === 'reports' && hasReports && (
          <ReportsPage
            store={store}
            persist={persist}
            showToast={() => {}}
            canSeeSocial={reportPerms.socialMedia}
            canSeePerfMarketing={reportPerms.perfMarketing}
          />
        )}

        {/* ══ COMPANY ATTENDANCE ══ */}
        {tab === 'attendance' && <CompanyAttendancePage store={store} canSeeTimes={false} />}
        </div>

        </main>
      </div>
    </div>
  );
}
